import { useState, useEffect } from 'react';
import { fetchLeaderboard, LeaderboardEntry } from '../services/leaderboardService';
import styles from './Lobby.module.css';

export interface LobbyProps {
  onPlay: () => void;
  onProfile: () => void;
}

export function Lobby({ onPlay, onProfile }: LobbyProps) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadLeaderboard() {
      try {
        const data = await fetchLeaderboard();
        if (!cancelled) {
          setLeaderboard(data);
          setError('');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={styles.lobbyPage}>
      <header className={styles.lobbyHeader}>
        <h1 className={styles.title}>Tic-Tac-Toadstool 🍄</h1>
        <p className={styles.subtitle}>Enter the mushroom forest and challenge an opponent</p>
      </header>

      <button
        type="button"
        className={styles.playButton}
        onClick={onPlay}
      >
        Play
      </button>

      <button
        type="button"
        className={styles.profileLink}
        onClick={onProfile}
        aria-label="Edit profile"
      >
        🍄 Profile
      </button>

      <section className={styles.leaderboardPanel} aria-label="Leaderboard">
        <h2 className={styles.leaderboardTitle}>🏆 Top Players</h2>

        {loading && (
          <p className={styles.leaderboardLoading}>Loading leaderboard...</p>
        )}

        {error && !loading && (
          <p className={styles.leaderboardError}>{error}</p>
        )}

        {!loading && !error && leaderboard.length === 0 && (
          <p className={styles.leaderboardEmpty}>No players on the leaderboard yet.</p>
        )}

        {!loading && !error && leaderboard.length > 0 && (
          <ol className={styles.leaderboardList}>
            {leaderboard.map((entry) => {
              const filename = entry.avatar.toLowerCase().replace(/\s+/g, '-');
              return (
                <li key={entry.rank} className={styles.leaderboardEntry}>
                  <span className={styles.rank}>#{entry.rank}</span>
                  <img
                    className={styles.entryAvatar}
                    src={`/avatars/${filename}.svg`}
                    alt={entry.avatar}
                  />
                  <span className={styles.entryNickname}>{entry.nickname}</span>
                  <span className={styles.entryWins}>
                    {entry.totalWins} {entry.totalWins === 1 ? 'win' : 'wins'}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

export default Lobby;
